import classNames from 'classnames';
import { useEffect, useRef, useState } from 'react';

import { useProgressService } from '@hooks/useProgressService';
import { GoalProgressModel } from '@models/progress';
import { setupForDark } from '@theme';

interface GoalProgressBarProps extends React.HTMLAttributes<HTMLDivElement> {
  goalEntityId: string;
  progress?: GoalProgressModel;
  height?: number;
  /** When true (default), Pusher store value takes precedence over the prop.
   *  Set to false in contexts showing another user's subscription to avoid
   *  cross-subscription collisions (e.g. mentor view). */
  enableRealtimeUpdates?: boolean;
}

function GoalProgressBar({
  goalEntityId,
  progress: progressProp,
  height = 8,
  enableRealtimeUpdates = true,
  className = '',
  ...props
}: GoalProgressBarProps) {
  // Store holds the current user's progress (from Pusher). It takes precedence
  // when realtime is enabled because it is fresher than the initial API prop.
  // Disable realtime when rendering another user's subscription (mentor view)
  // to avoid cross-subscription collisions for the same goalEntityId.
  const { getCachedGoalProgress, getGoalProgress } = useProgressService();
  const storeProgress = enableRealtimeUpdates ? getCachedGoalProgress(goalEntityId) : undefined;
  const progress = storeProgress || progressProp || null;

  // Self-fetch: when neither prop nor store has progress, fetch from API.
  // Covers cases like /explore where the lightweight GoalModel doesn't include progress.
  const hasFetched = useRef(false);
  useEffect(() => {
    if (!progress && enableRealtimeUpdates && !hasFetched.current) {
      hasFetched.current = true;
      getGoalProgress(goalEntityId);
    }
  }, [progress, enableRealtimeUpdates, goalEntityId, getGoalProgress]);

  // Pulse animation on change
  const [isPulsing, setIsPulsing] = useState(false);
  const prevProgressRef = useRef<GoalProgressModel | null>(null);

  useEffect(() => {
    if (
      prevProgressRef.current &&
      progress &&
      (prevProgressRef.current.low !== progress.low ||
        prevProgressRef.current.medium !== progress.medium ||
        prevProgressRef.current.high !== progress.high)
    ) {
      setIsPulsing(true);
      const timer = setTimeout(() => setIsPulsing(false), 600);
      return () => clearTimeout(timer);
    }
    prevProgressRef.current = progress;
  }, [progress?.low, progress?.medium, progress?.high]);

  if (!progress) return null;

  const total = Math.max(progress.total ?? 0, 0);
  const high = Math.max(progress.high ?? 0, 0);
  const medium = Math.max(progress.medium ?? 0, 0);
  const low = Math.max(progress.low ?? 0, 0);
  const sum = high + medium + low;

  if (total === 0 || sum === 0) return null;

  const scale = sum > total ? total / sum : 1;
  const highPct = ((high * scale) / total) * 100;
  const mediumPct = ((medium * scale) / total) * 100;
  const lowPct = ((low * scale) / total) * 100;

  return (
    <div
      className={classNames(
        'w-full flex flex-row overflow-hidden rounded-full',
        { 'animate-pulse-once': isPulsing },
        className
      )}
      style={{ height: `${height}px` }}
      {...props}
    >
      {/* High — light blue (leftmost) */}
      <div
        className={setupForDark('bg-progress-high')}
        style={{
          width: `${highPct}%`,
          transition: 'width 600ms ease-out'
        }}
      />
      {/* Medium — blue */}
      <div
        className={setupForDark('bg-progress-medium')}
        style={{
          width: `${mediumPct}%`,
          transition: 'width 600ms ease-out'
        }}
      />
      {/* Low — dark blue */}
      <div
        className={setupForDark('bg-progress-low')}
        style={{
          width: `${lowPct}%`,
          transition: 'width 600ms ease-out'
        }}
      />
      {/* Remaining — gray */}
      <div className={classNames('flex-1', setupForDark('bg-progress-none'))} />
    </div>
  );
}

export default GoalProgressBar;
